import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { textIncludesQuery } from '@narrativebench/editor-core';
import { BarChart3, Loader2, UserRound } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { PANEL_TABLE_SCROLL_STYLE } from '../constants/layout';
import { stripHtml } from '../utils/note-utils';
import { RICH_TEXT_HTML_CLASS } from '../utils/rich-text-html';
import { intersectIds } from '../utils/resource-bulk-assign';
import {
  EDITABLE_ENTITY_RELEVANCE_LEVELS,
  entityRelevanceLevelLabelKey,
  getEntityRelevanceLevel,
  relevanceScoreForLevel,
  type EntityRelevanceLevel,
} from '../utils/entity-relevance';
import { useShiftClickRowSelection } from '../hooks/useShiftClickRowSelection';
import { cn } from '../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Input } from './ui/input';
import { SortableTableHeader } from './SortableTableHeader';
import { EntityRelevanceDisplay } from './EntityRelevanceDisplay';
import { AnalyzeButton } from './AnalyzeButton';
import { ClearFiltersButton } from './ClearFiltersButton';
import { MultiSelectFilterDropdown } from './MultiSelectFilterDropdown';
import { DataTableRowCheckbox } from './DataTableRowCheckbox';
import { DataTableSelectAllCheckbox } from './DataTableSelectAllCheckbox';
import { DataTableSelectionActions } from './DataTableSelectionActions';
import { ConfirmDialog } from './ConfirmDialog';
import { RelatedEntityLink } from './RelatedEntityLink';
import type { RelatedEntity } from '../utils/entity-utils';

export interface CharactersTableRow {
  id: string;
  name: string;
  description?: string;
  role?: string;
  notes?: string;
  userNotes?: string;
  inconsistencies?: string;
  improvements?: string;
  isManual?: boolean;
  relevance?: number;
  referencedIn?: RelatedEntity[];
}

export type CharactersBulkPatch = {
  role?: string;
  relevance?: number | null;
};

const REFERENCED_IN_PREVIEW_LIMIT = 5;

interface CharactersTableProps {
  characters: CharactersTableRow[];
  analyzingCharacterId: string | null;
  onRowClick: (character: CharactersTableRow, event: React.MouseEvent) => void;
  onAnalyzeCharacter: (character: CharactersTableRow) => void;
  analyzeTooltip: string;
  canEdit?: boolean;
  onBulkPatch?: (characterIds: string[], patch: CharactersBulkPatch) => Promise<void>;
  onBulkDelete?: (characterIds: string[]) => Promise<void>;
  projectId?: string;
  onSeeAllReferencedIn?: (character: CharactersTableRow) => void;
}

const IMPORTANCE_FILTER_LEVELS: EntityRelevanceLevel[] = [
  'unknown',
  ...EDITABLE_ENTITY_RELEVANCE_LEVELS,
];

export function characterMatchesTextFilter(
  character: CharactersTableRow,
  filter: string
): boolean {
  const query = filter.trim();
  if (!query) {
    return true;
  }
  return (
    textIncludesQuery(character.name, query) ||
    textIncludesQuery(stripHtml(character.description ?? ''), query) ||
    textIncludesQuery(stripHtml(character.userNotes ?? ''), query) ||
    textIncludesQuery(stripHtml(character.notes ?? ''), query)
  );
}

export const CharactersTable: React.FC<CharactersTableProps> = ({
  characters,
  analyzingCharacterId,
  onRowClick,
  onAnalyzeCharacter,
  analyzeTooltip,
  canEdit = false,
  onBulkPatch,
  onBulkDelete,
  projectId,
  onSeeAllReferencedIn,
}) => {
  const { t } = useLocale();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'relevance', desc: true }]);
  const [textFilter, setTextFilter] = useState('');
  const [selectedImportanceLevels, setSelectedImportanceLevels] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const getVisibleRowIdsRef = useRef<() => string[]>(() => []);
  const {
    rowSelection,
    setRowSelection,
    selectedIds: selectedCharacterIds,
    selectRangeTo,
    toggleRowSelected,
    clearSelection,
    setAnchorRowId,
    handleRowClick,
  } = useShiftClickRowSelection({
    getVisibleRowIds: () => getVisibleRowIdsRef.current(),
  });

  const uniqueRoles = useMemo(() => {
    const roles = characters
      .map((character) => character.role?.trim())
      .filter((role): role is string => Boolean(role));
    return [...new Set(roles)].sort((a, b) => a.localeCompare(b));
  }, [characters]);

  const filteredCharacters = useMemo(() => {
    return characters.filter((character) => {
      if (!characterMatchesTextFilter(character, textFilter)) {
        return false;
      }
      if (selectedImportanceLevels.length > 0) {
        const level = getEntityRelevanceLevel(character.relevance);
        if (!selectedImportanceLevels.includes(level)) {
          return false;
        }
      }
      if (selectedRoles.length > 0) {
        const role = character.role?.trim() ?? '';
        if (!selectedRoles.includes(role)) {
          return false;
        }
      }
      return true;
    });
  }, [characters, textFilter, selectedImportanceLevels, selectedRoles]);

  const hasSelection = canEdit && selectedCharacterIds.length > 0;

  useEffect(() => {
    const visibleIds = new Set(filteredCharacters.map((character) => character.id));
    setRowSelection((prev) => {
      let changed = false;
      const next: typeof prev = {};
      for (const [id, selected] of Object.entries(prev)) {
        if (selected && visibleIds.has(id)) {
          next[id] = true;
        } else if (selected) {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [filteredCharacters, setRowSelection]);

  useEffect(() => {
    if (!hasSelection || bulkAssigning) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        clearSelection();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasSelection, bulkAssigning, clearSelection]);

  const assignmentImportanceLevels = useMemo(() => {
    if (!hasSelection) {
      return selectedImportanceLevels;
    }
    return intersectIds(
      selectedCharacterIds.map((id) => {
        const character = characters.find((item) => item.id === id);
        return character ? [getEntityRelevanceLevel(character.relevance)] : [];
      })
    );
  }, [hasSelection, selectedCharacterIds, characters, selectedImportanceLevels]);

  const assignmentRoles = useMemo(() => {
    if (!hasSelection) {
      return selectedRoles;
    }
    return intersectIds(
      selectedCharacterIds.map((id) => {
        const role = characters.find((item) => item.id === id)?.role?.trim();
        return role ? [role] : [];
      })
    );
  }, [hasSelection, selectedCharacterIds, characters, selectedRoles]);

  const runBulkPatch = useCallback(
    async (patch: CharactersBulkPatch): Promise<void> => {
      if (!onBulkPatch || bulkAssigning) {
        return;
      }
      setBulkAssigning(true);
      try {
        await onBulkPatch(selectedCharacterIds, patch);
      } finally {
        setBulkAssigning(false);
      }
    },
    [onBulkPatch, bulkAssigning, selectedCharacterIds]
  );

  const handleImportanceChange = useCallback(
    (nextIds: string[]): void => {
      if (!hasSelection || !onBulkPatch) {
        setSelectedImportanceLevels(nextIds);
        return;
      }
      if (bulkAssigning) {
        return;
      }
      const added = nextIds.filter((id) => !assignmentImportanceLevels.includes(id));
      if (added.length === 0) {
        return;
      }
      const level = added[added.length - 1] as EntityRelevanceLevel;
      const relevance = relevanceScoreForLevel(level) ?? null;
      void runBulkPatch({ relevance });
    },
    [
      hasSelection,
      onBulkPatch,
      bulkAssigning,
      assignmentImportanceLevels,
      runBulkPatch,
    ]
  );

  const requestDeleteSelected = useCallback((): void => {
    if (selectedCharacterIds.length === 0 || !onBulkDelete || bulkAssigning) {
      return;
    }
    setPendingDeleteIds([...selectedCharacterIds]);
  }, [selectedCharacterIds, onBulkDelete, bulkAssigning]);

  const confirmDeleteSelected = useCallback(async (): Promise<void> => {
    const ids = pendingDeleteIds;
    setPendingDeleteIds([]);
    if (ids.length === 0 || !onBulkDelete) {
      return;
    }
    setBulkDeleting(true);
    try {
      await onBulkDelete(ids);
      clearSelection();
    } finally {
      setBulkDeleting(false);
    }
  }, [pendingDeleteIds, onBulkDelete, clearSelection]);

  const handleRolesChange = useCallback(
    (nextIds: string[]): void => {
      if (!hasSelection || !onBulkPatch) {
        setSelectedRoles(nextIds);
        return;
      }
      if (bulkAssigning) {
        return;
      }
      const added = nextIds.filter((id) => !assignmentRoles.includes(id));
      if (added.length === 0) {
        return;
      }
      void runBulkPatch({ role: added[added.length - 1] });
    },
    [hasSelection, onBulkPatch, bulkAssigning, assignmentRoles, runBulkPatch]
  );

  const importanceOptions = useMemo(
    () =>
      (hasSelection ? EDITABLE_ENTITY_RELEVANCE_LEVELS : IMPORTANCE_FILTER_LEVELS).map((level) => ({
        id: level,
        name: t(entityRelevanceLevelLabelKey(level)),
      })),
    [hasSelection, t]
  );

  const roleOptions = useMemo(() => {
    const roles = hasSelection
      ? [...new Set([...uniqueRoles, ...assignmentRoles])]
      : uniqueRoles;
    return roles
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((role) => ({ id: role, name: role }));
  }, [hasSelection, uniqueRoles, assignmentRoles]);

  const hasActiveFilters =
    textFilter.trim() !== '' ||
    selectedImportanceLevels.length > 0 ||
    selectedRoles.length > 0;

  const clearFilters = useCallback((): void => {
    setTextFilter('');
    setSelectedImportanceLevels([]);
    setSelectedRoles([]);
  }, []);

  const assignModeClassName = hasSelection
    ? 'border-[var(--scribane-green)] bg-[rgba(94,204,128,0.08)]'
    : undefined;

  const columns = useMemo<ColumnDef<CharactersTableRow>[]>(() => {
    const cols: ColumnDef<CharactersTableRow>[] = [];
    if (canEdit) {
      cols.push({
        id: 'select',
        enableSorting: false,
        header: ({ table }) => (
          <DataTableSelectAllCheckbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(checked) => {
              table.toggleAllPageRowsSelected(checked);
              const visibleIds = getVisibleRowIdsRef.current();
              setAnchorRowId(checked && visibleIds.length > 0 ? visibleIds[0] : null);
            }}
            label={t('characters_select_all')}
          />
        ),
        cell: ({ row }) => (
          <DataTableRowCheckbox
            checked={row.getIsSelected()}
            rowId={row.id}
            ariaLabel={t('characters_select_row')}
            onSelectRange={selectRangeTo}
            onToggle={toggleRowSelected}
          />
        ),
        size: 40,
      });
    }
    cols.push(
      {
        accessorKey: 'name',
        header: ({ column }) => <SortableTableHeader label={t('name')} column={column} />,
        cell: ({ row }) => (
          <span className="min-w-[18rem] font-medium whitespace-nowrap">{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'relevance',
        header: ({ column }) => (
          <SortableTableHeader label={t('entity_relevance')} column={column} />
        ),
        cell: ({ row }) => <EntityRelevanceDisplay value={row.original.relevance} />,
        sortingFn: (rowA, rowB) =>
          (rowA.original.relevance ?? 0) - (rowB.original.relevance ?? 0),
      },
      {
        accessorKey: 'role',
        header: ({ column }) => (
          <SortableTableHeader label={t('character_role')} column={column} />
        ),
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.role || '—'}</span>
        ),
        sortingFn: (rowA, rowB) =>
          (rowA.original.role || '').localeCompare(rowB.original.role || ''),
      },
      {
        accessorKey: 'description',
        header: ({ column }) => (
          <SortableTableHeader label={t('description')} column={column} />
        ),
        cell: ({ row }) =>
          row.original.description ? (
            <div
              className={`line-clamp-2 max-w-md text-muted-foreground ${RICH_TEXT_HTML_CLASS} [&_p]:my-0`}
              dangerouslySetInnerHTML={{ __html: row.original.description }}
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        sortingFn: (rowA, rowB) =>
          stripHtml(rowA.original.description || '').localeCompare(
            stripHtml(rowB.original.description || '')
          ),
      },
      {
        id: 'referenced_in',
        accessorFn: (row) => row.referencedIn?.length ?? 0,
        header: ({ column }) => (
          <SortableTableHeader label={t('referenced_in')} column={column} />
        ),
        cell: ({ row }) => {
          const entities = row.original.referencedIn ?? [];
          if (entities.length === 0) {
            return <span className="text-muted-foreground">—</span>;
          }
          const visibleEntities = entities.slice(0, REFERENCED_IN_PREVIEW_LIMIT);
          const hasMore = entities.length > REFERENCED_IN_PREVIEW_LIMIT;
          return (
            <div
              className="flex max-w-xs flex-wrap items-center gap-1.5"
              onClick={(event) => event.stopPropagation()}
            >
              {visibleEntities.map((entity) => (
                <RelatedEntityLink
                  key={`${entity.type}-${entity.id}-${entity.documentId ?? ''}`}
                  entity={entity}
                  projectId={projectId ?? ''}
                />
              ))}
              {hasMore && onSeeAllReferencedIn ? (
                <button
                  type="button"
                  className="text-xs font-medium text-scribane-green hover:underline"
                  onClick={() => onSeeAllReferencedIn(row.original)}
                >
                  {t('see_all')}
                </button>
              ) : null}
            </div>
          );
        },
        sortingFn: (rowA, rowB) =>
          (rowA.original.referencedIn?.length ?? 0) -
          (rowB.original.referencedIn?.length ?? 0),
      },
      {
        id: 'analysis',
        accessorFn: (row) => Boolean(row.inconsistencies || row.improvements),
        enableSorting: false,
        header: () => <span className="px-2 py-1">{t('analysis')}</span>,
        cell: ({ row }) => {
          const character = row.original;
          const hasAnalysis = Boolean(character.inconsistencies || character.improvements);
          return (
            <div
              className="flex items-center gap-2"
              onClick={(event) => event.stopPropagation()}
            >
              <AnalyzeButton
                onClick={() => onAnalyzeCharacter(character)}
                isAnalyzing={analyzingCharacterId === character.id}
                tooltip={analyzeTooltip}
                matchLabelWidth={t('reanalyze')}
              >
                {hasAnalysis ? t('reanalyze') : t('analyze')}
              </AnalyzeButton>
            </div>
          );
        },
      }
    );
    return cols;
  }, [
    t,
    canEdit,
    analyzingCharacterId,
    onAnalyzeCharacter,
    analyzeTooltip,
    projectId,
    onSeeAllReferencedIn,
    selectRangeTo,
    toggleRowSelected,
    setAnchorRowId,
  ]);

  const table = useReactTable({
    data: filteredCharacters,
    columns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: canEdit,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  getVisibleRowIdsRef.current = (): string[] =>
    table.getRowModel().rows.map((row) => row.id);

  return (
    <>
      <div
        className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
        aria-busy={bulkAssigning || undefined}
      >
        {bulkAssigning ? (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background/70 backdrop-blur-[1px]"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
            <p className="text-sm font-medium text-foreground">{t('characters_assigning')}</p>
          </div>
        ) : null}
        <div
          className={cn(
            'mb-4 flex flex-wrap items-center gap-3',
            bulkAssigning && 'pointer-events-none'
          )}
        >
          <Input
            type="search"
            placeholder={t('search_characters')}
            value={textFilter}
            onChange={(event) => setTextFilter(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            className="min-w-0 flex-1 bg-background sm:max-w-xs"
            autoComplete="off"
            disabled={bulkAssigning}
          />
          <MultiSelectFilterDropdown
            label={hasSelection ? t('assign_importance') : t('entity_relevance')}
            icon={BarChart3}
            options={importanceOptions}
            selectedIds={assignmentImportanceLevels}
            onChange={handleImportanceChange}
            className={assignModeClassName}
            disabled={bulkAssigning}
          />
          {(roleOptions.length > 0 || hasSelection) && (
            <MultiSelectFilterDropdown
              label={hasSelection ? t('assign_role') : t('character_role')}
              icon={UserRound}
              options={roleOptions}
              selectedIds={assignmentRoles}
              onChange={handleRolesChange}
              className={assignModeClassName}
              createLabel={
                hasSelection ? (role) => `${t('Create role')} ${role}` : undefined
              }
              disabled={bulkAssigning}
            />
          )}
          <ClearFiltersButton
            enabled={hasActiveFilters && !bulkAssigning}
            onClick={clearFilters}
          />
          <DataTableSelectionActions
            selectedCount={hasSelection ? selectedCharacterIds.length : 0}
            countLabel={t('characters_selected_count').replace(
              '{count}',
              String(selectedCharacterIds.length)
            )}
            onClear={clearSelection}
            onDelete={onBulkDelete ? requestDeleteSelected : undefined}
            deleting={bulkDeleting}
            disabled={bulkAssigning}
          />
        </div>
        <div
          style={PANEL_TABLE_SCROLL_STYLE}
          className={cn(bulkAssigning && 'pointer-events-none')}
        >
          <Table className="min-w-[800px]">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(header.column.id === 'select' && 'w-10 pl-4 pr-0')}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="py-12 text-center text-muted-foreground"
                  >
                    {t('characters_no_matches')}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                    className={cn(
                      'cursor-pointer',
                      row.getIsSelected() && 'bg-muted'
                    )}
                    onClick={(event) => {
                      if (bulkAssigning) {
                        return;
                      }
                      if (canEdit) {
                        handleRowClick(row.id, event, () => onRowClick(row.original, event));
                        return;
                      }
                      onRowClick(row.original, event);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(cell.column.id === 'select' && 'w-10 pl-4 pr-0')}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <ConfirmDialog
        open={pendingDeleteIds.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDeleteIds([]);
          }
        }}
        title={
          pendingDeleteIds.length === 1
            ? t('delete_character')
            : t('delete_characters')
        }
        description={
          pendingDeleteIds.length === 1
            ? t('confirm_delete_character')
            : t('confirm_delete_characters').replace(
                '{count}',
                String(pendingDeleteIds.length)
              )
        }
        confirmLabel={
          pendingDeleteIds.length === 1 ? t('delete_character') : t('delete')
        }
        onConfirm={() => {
          void confirmDeleteSelected();
        }}
      />
    </>
  );
};
